import { css } from "@emotion/react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

export interface ReportItem {
  scan_id: string;
  target_name: string;
  target_id?: string | number;
  created_at?: string;
  total: number;
  passed: number;
  failed: number;
  score: number;
  plugin_type?: string;
  summary?: any;
}

const pageContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 24px 32px 48px 32px;
  background-color: #f8fafc;
  box-sizing: border-box;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
`;

const headerContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const titleArea = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const titleText = css`
  margin: 0;
  font-size: 1.55rem;
  font-weight: 850;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const subtitleText = css`
  margin: 0;
  font-size: 0.88rem;
  color: #64748b;
  font-weight: 500;
`;

const headerActions = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const outlineBtn = css`
  height: 36px;
  padding: 0 14px;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #94a3b8;
    color: #0f172a;
  }
`;

const primaryBtn = css`
  height: 36px;
  padding: 0 16px;
  background-color: #059669;
  border: 1px solid #059669;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 750;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
  transition: all 0.15s ease;

  &:hover {
    background-color: #047857;
    border-color: #047857;
  }
`;

const metricsGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const metricCard = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const metricLabel = css`
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
`;

const metricValue = css`
  font-size: 1.5rem;
  font-weight: 850;
  color: #0f172a;
`;

const searchInput = css`
  width: 100%;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #0f172a;
  background-color: #ffffff;
  margin-bottom: 20px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #059669;
  }
`;

const reportsGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 18px;
`;

const reportCard = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.06);
  }
`;

const reportCardHeader = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const reportTargetTitle = css`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const reportDateText = css`
  margin: 2px 0 0 0;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
`;

const scoreBadge = (score: number) => {
  if (score >= 80) {
    return css`
      padding: 4px 10px;
      border-radius: 9999px;
      background-color: #ecfdf5;
      border: 1px solid #bbf7d0;
      color: #15803d;
      font-size: 0.78rem;
      font-weight: 800;
      flex-shrink: 0;
    `;
  }
  if (score >= 50) {
    return css`
      padding: 4px 10px;
      border-radius: 9999px;
      background-color: #fffbeb;
      border: 1px solid #fde68a;
      color: #b45309;
      font-size: 0.78rem;
      font-weight: 800;
      flex-shrink: 0;
    `;
  }
  return css`
    padding: 4px 10px;
    border-radius: 9999px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 0.78rem;
    font-weight: 800;
    flex-shrink: 0;
  `;
};

const probeStatRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #f8fafc;
  border-radius: 8px;
  font-size: 0.78rem;
`;

const actionRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
`;

const emptyStateContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 56px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
`;

export function ReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const items: ReportItem[] = [];

      // 1. Fetch from list-scans
      const scansRes = await authFetch(prependBasename("/v1/security/targets/evaluations/list-scans"));
      if (scansRes.ok) {
        const scansData = await scansRes.json();
        if (Array.isArray(scansData)) {
          scansData.forEach((s) => {
            if (s && s.scan_id) {
              const total = s.total ?? 0;
              const passed = s.passed ?? 0;
              const failed = s.failed ?? 0;
              const score = s.score ?? (total > 0 ? Math.round((passed / total) * 100) : 100);
              items.push({
                scan_id: s.scan_id,
                target_name: s.target_name || s.plugin_type || "Security Evaluation Scan",
                target_id: s.target_id,
                created_at: s.created_at,
                total,
                passed,
                failed,
                score,
                plugin_type: s.plugin_type,
                summary: s.summary,
              });
            }
          });
        }
      }

      // 2. Also check saved targets with last_scan_id
      try {
        const targetsRes = await authFetch(prependBasename("/v1/security/targets"));
        if (targetsRes.ok) {
          const targetsData = await targetsRes.json();
          if (Array.isArray(targetsData)) {
            targetsData.forEach((t) => {
              if (t.last_scan_id && !items.some((i) => i.scan_id === t.last_scan_id)) {
                items.push({
                  scan_id: t.last_scan_id,
                  target_name: t.name || "Target Evaluation",
                  target_id: t.id,
                  created_at: t.last_scan_date,
                  total: 0,
                  passed: 0,
                  failed: 0,
                  score: t.last_scan_score ?? 100,
                  plugin_type: t.type,
                });
              }
            });
          }
        }
      } catch (err) {}

      // Sort newest first
      items.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });

      setReports(items);
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to load security evaluation reports");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleExportHTML = async (scanId: string) => {
    try {
      const res = await authFetch(prependBasename(`/v1/security/targets/evaluations/scan/${scanId}/report.html`));
      if (res.ok) {
        const html = await res.text();
        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `security-evaluation-${scanId}.html`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Failed to export HTML report", e);
    }
  };

  const handleExportJSON = async (scanId: string) => {
    try {
      const res = await authFetch(prependBasename(`/v1/security/targets/evaluations/scan/${scanId}`));
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `security-report-${scanId}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Failed to export JSON report", e);
    }
  };

  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    const q = searchQuery.toLowerCase();
    return reports.filter(
      (r) =>
        r.target_name.toLowerCase().includes(q) ||
        r.scan_id.toLowerCase().includes(q) ||
        (r.plugin_type && r.plugin_type.toLowerCase().includes(q))
    );
  }, [reports, searchQuery]);

  const totalReports = reports.length;
  const reportsWithFindings = reports.filter((r) => r.failed > 0).length;
  const avgPosture = totalReports > 0
    ? Math.round(reports.reduce((acc, r) => acc + r.score, 0) / totalReports)
    : 100;

  return (
    <div css={pageContainer} data-testid="security-reports-page">
      {/* Header */}
      <div css={headerContainer}>
        <div css={titleArea}>
          <h1 css={titleText}>Security Reports</h1>
          <p css={subtitleText}>
            Comprehensive security evaluation reports, vulnerability verdicts, and remediation guidance.
          </p>
        </div>
        <div css={headerActions}>
          <button type="button" css={outlineBtn} onClick={fetchReports} disabled={isLoading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            <span>Refresh</span>
          </button>
          <button type="button" css={primaryBtn} onClick={() => navigate("/red-teaming")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Run Security Scan</span>
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div css={metricsGrid}>
        <div css={metricCard}>
          <span css={metricLabel}>Total Saved Reports</span>
          <span css={metricValue}>{totalReports}</span>
        </div>
        <div css={metricCard}>
          <span css={metricLabel}>Vulnerabilities Detected</span>
          <span css={metricValue} style={{ color: reportsWithFindings > 0 ? "#dc2626" : "#059669" }}>
            {reportsWithFindings}
          </span>
        </div>
        <div css={metricCard}>
          <span css={metricLabel}>Avg Security Posture</span>
          <span css={metricValue} style={{ color: avgPosture >= 80 ? "#059669" : avgPosture >= 50 ? "#d97706" : "#dc2626" }}>
            {avgPosture}%
          </span>
        </div>
      </div>

      {/* Search Input */}
      <input
        type="text"
        placeholder="Search reports by target name, scan ID, or plugin..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        css={searchInput}
      />

      {/* Reports List */}
      {isLoading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "#64748b", fontSize: "0.9rem" }}>
          Loading evaluation reports from backend...
        </div>
      ) : errorMessage ? (
        <div style={{ padding: "24px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "0.88rem" }}>
          <strong>Error loading reports:</strong> {errorMessage}
        </div>
      ) : filteredReports.length === 0 ? (
        <div css={emptyStateContainer}>
          <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>
            📊
          </div>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>
            {reports.length === 0 ? "No Evaluation Reports Found" : "No Reports Match Your Search"}
          </h3>
          <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", maxWidth: "480px" }}>
            {reports.length === 0
              ? "Completed security scans generate full interactive reports with vulnerability breakdowns, attack transcripts, and exportable findings."
              : "Try adjusting your search query to see other reports."}
          </p>
          <button type="button" css={primaryBtn} onClick={() => navigate("/red-teaming")} style={{ marginTop: "8px" }}>
            Launch Active Scan
          </button>
        </div>
      ) : (
        <div css={reportsGrid}>
          {filteredReports.map((report) => {
            const timeStr = report.created_at ? new Date(report.created_at).toLocaleString() : "Recent";
            return (
              <div key={report.scan_id} css={reportCard}>
                <div css={reportCardHeader}>
                  <div>
                    <h3 css={reportTargetTitle}>{report.target_name}</h3>
                    <p css={reportDateText}>Scan ID: <code style={{ fontFamily: "monospace", color: "#334155" }}>{report.scan_id}</code> • {timeStr}</p>
                  </div>
                  <span css={scoreBadge(report.score)}>{report.score}%</span>
                </div>

                <div css={probeStatRow}>
                  <span style={{ color: "#64748b", fontWeight: 600 }}>Test Probes</span>
                  <span>
                    <strong style={{ color: "#059669" }}>{report.passed}</strong> passed / {report.total} total
                    {report.failed > 0 && (
                      <span style={{ color: "#dc2626", fontWeight: 700, marginLeft: "6px" }}>
                        ({report.failed} vulnerabilities)
                      </span>
                    )}
                  </span>
                </div>

                <div css={actionRow}>
                  <button
                    type="button"
                    css={primaryBtn}
                    style={{ flex: 1, height: "32px", fontSize: "0.8rem", justifyContent: "center" }}
                    onClick={() => navigate(`/red-teaming?scanId=${encodeURIComponent(report.scan_id)}`)}
                  >
                    View Full Report
                  </button>
                  <button
                    type="button"
                    css={outlineBtn}
                    style={{ height: "32px", padding: "0 10px", fontSize: "0.75rem" }}
                    title="Export Standalone HTML Report"
                    onClick={() => handleExportHTML(report.scan_id)}
                  >
                    HTML
                  </button>
                  <button
                    type="button"
                    css={outlineBtn}
                    style={{ height: "32px", padding: "0 10px", fontSize: "0.75rem" }}
                    title="Export Full JSON Report"
                    onClick={() => handleExportJSON(report.scan_id)}
                  >
                    JSON
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default ReportsPage;
